// @ts-nocheck
import { graphql } from "gatsby"
import { useEffect, useMemo, useState } from "react"

import loadable from "@loadable/component"
import { Box } from "@mui/material"
import { get, isEmpty } from "lodash"

import { addNode, removeNode, useDispatch, useSelector } from "@ggs/store"
import { Container, TripCollection } from "../components"
import PreFooterSliceResolver from "../components/PreFooter/PreFooterSliceResolver"
import SEOHead from "../components/head"
import Layout from "../components/layout"
import {
  useArticleCategoryData,
  useGeoData,
  useGlobalAdsSolrData,
  useStaffData,
  useTripsData,
} from "../local/hooks"

import {
  mapArticleTeaser,
  mapCareersListItems,
  mapExploreListItems,
  mapLandingPageData,
  mapPlacesItems,
  mapSectionSwitcher,
  mapTravelStyleItems,
  mapWishlistData,
} from "../helpers/data-mappers"

// landing page components
import {
  AwardsList,
  BannerCTA,
  BenefitsList,
  BlogSubNavigation,
  BrochuresCollection,
  CareersList,
  ContactCTA,
  ContentHighlight,
  DestinationsCollection,
  ExploreList,
  FeaturedLogos,
  GeoEntityProps,
  Hero,
  ImageOverview,
  LocationBanners,
  PerksList,
  SectionSwitcher,
  Slideshow,
  SlideshowItemType,
  StackedCTA,
  SuggestedTrips,
  Teaser,
  TeaserCollection,
  Text,
  TextOverview,
  TripGeneralSearch,
  TripStyles,
  VisualMediaContent,
} from "../components"
import useArticlesSolrData from "../local/hooks/useArticlesSolrData"

const Map = loadable(() => import("../components/Map/Map"))
const ParallaxMedia = loadable(() => import("../components/ParallaxMedia/ParallaxMedia"))
const WorldMap = loadable(() => import("../components/WorldMap/WorldMap"))

export default function LandingPageView(props) {
  const landingPage = get(props, "data.nodeLandingPage", {})
  const dispatch = useDispatch()
  const wishlist = useSelector((state) => state.views.wishlist)
  const likedArticles = wishlist?.article && wishlist?.article.map((article) => article.nid)
  const likedTrips = wishlist?.trip && wishlist?.trip.map((trip) => trip.nid)
  const { getStaffData, getRandomStaff } = useStaffData()
  const { getGeoDestinationData, getGeoCountriesByDestinationData } = useGeoData()
  const { getArticleCategoryData } = useArticleCategoryData()
  const { getGenericGlobalAds } = useGlobalAdsSolrData()
  const { getTripsData, getGroupTripsByDestination, getTripsBySpecial } = useTripsData()
  const [articles, setArticles] = useState([])
  const { getArticles } = useArticlesSolrData()
  const [staffSlideshowData, setStaffSlideshowData] = useState<SlideshowItemType[]>([])
  const [destinationData, setDestinationData] = useState<GeoEntityProps[]>(null)
  const [articleCategories, setArticleCategories] = useState(null)
  const [contactCtaStaff, setContactCtaStaff] = useState(null)
  const [globalAds, setGlobalAds] = useState(null)
  const [suggestedTrips, setSuggestedTrips] = useState(null)
  const [countriesCollections, setCountriesCollections] = useState(null)
  const [tripsCollection, setTripsCollection] = useState(null)
  const [specialTrips, setSpecialTrips] = useState(null)
  const usedComponentTypes = useMemo(() => {
    const landingPageComponents = get(landingPage, "components", [])
    return landingPageComponents.map((component) => component.type)
  }, [landingPage])
  const destinationCollectionComponents = useMemo(() => {
    const components = get(landingPage, "components", [])
    return components.filter((component) => component?.type === "destination_collection")
  }, [landingPage])
  const refDestinationId = get(landingPage, "destinations[0].external_id", "")
  const userCountry = useSelector((state) => state.settings.country)
  const pageSlug = get(landingPage, "slug", "")

  const fetchGroupTripsByDestination = (page = 1) => {
    const pageSize = 9
    const index = page === 1 ? 0 : (page - 1) * 9
    getGroupTripsByDestination(refDestinationId, index, pageSize).then((data) => {
      if (data?.items && !isEmpty(data.items)) {
        setTripsCollection(data)
      }
    })
  }

  const fetchSpecialTripsByDestination = (page = 1) => {
    const pageSize = 9
    const index = page === 1 ? 0 : (page - 1) * 9
    getTripsBySpecial(refDestinationId, index, pageSize).then((data) => {
      if (data?.items && !isEmpty(data.items)) {
        setSpecialTrips(data)
      }
    })
  }

  useEffect(() => {
    // If staff_slideshow is used, resolve staff data.
    if (usedComponentTypes.includes("staff_slideshow")) {
      getStaffData().then((data) => {
        setStaffSlideshowData(data)
      })
    }
    // If destination_map is used, resolve staff data.
    if (usedComponentTypes.includes("destination_map")) {
      getGeoDestinationData().then((data) => {
        setDestinationData({ items: data })
      })
    }
    // If blog_sub_nav is used, resolve staff data.
    if (usedComponentTypes.includes("blog_sub_nav")) {
      getArticleCategoryData().then((data) => {
        setArticleCategories(data)
      })
    }
    // If contact_cta is used, resolve staff data.
    if (usedComponentTypes.includes("teaser_3up")) {
      const component = components.find((component) => component.type === "teaser_3up")
      getArticles(component).then((data) => {
        const filteredData = mapWishlistData(likedArticles, data)
        setArticles(filteredData)
      })
    }
    // If contact_cta is used, resolve random staff members data.
    if (usedComponentTypes.includes("contact_cta")) {
      getRandomStaff().then((data) => {
        setContactCtaStaff(data)
      })
    }
    // If global_ad is used, resolve staff data.
    if (usedComponentTypes.includes("global_ad")) {
      getGenericGlobalAds().then((data) => {
        setGlobalAds(data)
      })
    }
    if (
      Array.isArray(destinationCollectionComponents) &&
      destinationCollectionComponents.length > 0
    ) {
      const promises = []
      destinationCollectionComponents.forEach((component) => {
        const result = getGeoCountriesByDestinationData(component?.destinations?.external_id)
        promises.push(result)
      })
      Promise.all(promises).then((values) => setCountriesCollections(values))
    }
  }, [])

  useEffect(() => {
    // If suggested_trips is used, resolve trip data.
    if (usedComponentTypes.includes("suggested_trips")) {
      getTripsData(refDestinationId).then((data) => {
        const filteredData = mapWishlistData(likedTrips, data)
        setSuggestedTrips({
          items: filteredData,
        })
      })
    }
    // If group_trips_by_destination is used, resolve trips data.
    if (usedComponentTypes.includes("group_trips_by_destination")) {
      fetchGroupTripsByDestination()
    }
    // If main_search is used, resolve trips data.
    if (usedComponentTypes.includes("trips_destination_specials")) {
      fetchSpecialTripsByDestination()
    }
  }, [userCountry])

  const addToWishList = (selectedEntity) => {
    if (selectedEntity.contentType === "article") {
      const filteredState = mapArticleTeaser(articles, selectedEntity, true)
      setArticles(filteredState)
    }
    if (selectedEntity.contentType === "trip") {
      const filteredState = mapArticleTeaser(suggestedTrips.items, selectedEntity, true)
      setSuggestedTrips({
        ...suggestedTrips,
        items: filteredState,
      })
    }
    dispatch(
      addNode({ type: selectedEntity.contentType, entity: selectedEntity, idPropertyKey: "nid" })
    )
  }

  const removeFromWishList = (selectedEntity) => {
    if (selectedEntity.contentType === "article") {
      const filteredState = mapArticleTeaser(articles, selectedEntity, false)
      setArticles(filteredState)
    }
    if (selectedEntity.contentType === "trip") {
      const filteredState = mapArticleTeaser(suggestedTrips.items, selectedEntity, false)
      setSuggestedTrips({
        ...suggestedTrips,
        items: filteredState,
      })
    }
    dispatch(removeNode({ type: selectedEntity.contentType, findPredicate: selectedEntity }))
  }

  const mappedData = mapLandingPageData(landingPage)
  if (!landingPage) {
    return <Box>{t("Page not found")}</Box>
  }

  const { hero, components } = mappedData

  const getCountriesCollection = (id: string) => {
    const countries = countriesCollections?.find((country) => country?.id === id)
    if (countries) return countries?.items
    return null
  }

  return (
    <>
      {ComposeHead(props)}
      <Layout
        hero={
          <Hero
            title={hero?.title}
            subtitle={hero?.subtitle}
            media={hero?.images}
            location={hero?.location}
            subtitleImageUrl={hero?.subtitleImageUrl}
            video={hero?.video}
            path={hero?.path}
            reducedHeight={hero?.short_version}
          />
        }
      >
        {components &&
          components.map((component) => {
            switch (component.type) {
              case "contact_cta":
                return (
                  <ContactCTA
                    key={component?.id}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    description={component?.description}
                    link={component?.link}
                    image={component?.image}
                    staff={contactCtaStaff}
                  />
                )
              case "text_overview":
                return (
                  <TextOverview
                    key={component?.id}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    layout={component?.layout || "full"}
                    description={component?.description}
                    link={component?.link}
                  />
                )
              case "parallax_media":
                return (
                  <ParallaxMedia
                    key={component?.id}
                    image_1={component?.image_1}
                    image_2={component?.image_2}
                    image_3={component?.image_3}
                    video={component?.video}
                  />
                )
              case "benefits_list":
                return <BenefitsList key={component?.id} items={component?.items} />
              case "trip_styles":
                return (
                  <TripStyles
                    key={component?.id}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    layout={component?.layout}
                    items={mapTravelStyleItems(component?.travel_items)}
                  />
                )
              case "places_slideshow":
                return (
                  <Slideshow
                    key={component?.id}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    items={mapPlacesItems(component?.places)}
                  />
                )
              case "suggested_trips":
                return (
                  <>
                    {suggestedTrips?.items?.length && (
                      <SuggestedTrips
                        key={component?.id}
                        title={component?.title}
                        subtitle={component?.subtitle}
                        link={{ title: "All Trips", uri: "/trips" }}
                        items={suggestedTrips.items}
                        onLike={addToWishList}
                        onUnlike={removeFromWishList}
                      />
                    )}
                  </>
                )
              case "teaser_3up":
                return (
                  <Teaser
                    key={component?.id}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    items={articles}
                    onLike={addToWishList}
                    onUnlike={removeFromWishList}
                  />
                )
              case "image_overview":
                return (
                  <ImageOverview
                    key={component?.id}
                    title={component?.title}
                    image={component?.image}
                    description={component?.description}
                    link={component?.link}
                  />
                )
              case "award_list":
                return (
                  <AwardsList
                    key={component?.id}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    items={component?.award_item}
                  />
                )
              case "stacked_cta":
                return (
                  <StackedCTA
                    key={component?.id}
                    direction={component?.direction}
                    items={component?.items}
                  />
                )
              case "content_highlight":
                return (
                  <ContentHighlight
                    key={component?.id}
                    color={component?.background_color}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    description={component?.description}
                    image={component?.image}
                    link={component?.link}
                  />
                )
              case "destination_collection":
                return (
                  <DestinationsCollection
                    key={component?.id}
                    items={getCountriesCollection(component?.destinations?.external_id)}
                  />
                )
              case "banner_cta":
                return (
                  <BannerCTA
                    key={component?.id}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    image={component?.image}
                    link={component?.link}
                  />
                )
              case "careers_list":
                return (
                  <CareersList
                    key={component?.id}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    link={component?.link}
                    items={mapCareersListItems(component?.job_items)}
                  />
                )
              case "visual_media_content":
                return <VisualMediaContent key={component?.id} images={component?.media} />
              case "featured_logos":
                return <FeaturedLogos key={component?.id} items={component?.logos} />
              case "perks_list":
                return (
                  <PerksList
                    key={component?.id}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    items={component?.items}
                  />
                )
              case "staff_slideshow":
                return (
                  <Slideshow
                    key={component?.id}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    items={staffSlideshowData}
                  />
                )
              case "section_switcher":
                return (
                  <SectionSwitcher
                    key={component?.id}
                    items={mapSectionSwitcher(component?.items)}
                  />
                )
              case "explore_list":
                return (
                  <ExploreList
                    key={component?.id}
                    title={component?.title}
                    subtitle={component?.subtitle}
                    description={component?.description}
                    items={mapExploreListItems(component?.items)}
                  />
                )
              case "text":
                return <Text key={component?.id} title={component?.title} text={component?.text} />
              case "destination_map":
                return (
                  <>{destinationData?.items && <Map key={component?.id} {...destinationData} />}</>
                )
              case "blog_sub_nav":
                return (
                  <BlogSubNavigation
                    key={component?.id}
                    title="Globetrotting"
                    items={articleCategories}
                  />
                )
              case "flex_card_deck":
                return <TeaserCollection key={component?.id} cards={component?.cards} />
              case "group_trips_by_destination":
                return tripsCollection?.items ? (
                  <Container maxWidth={1480}>
                    <TripCollection
                      currentPage={1}
                      onPageChange={fetchGroupTripsByDestination}
                      showSpecialTag={false}
                      {...tripsCollection}
                    />
                  </Container>
                ) : null
              case "resource_collection":
                return (
                  <BrochuresCollection
                    key={component?.id}
                    title={component?.title}
                    count={component?.items?.length}
                    items={component?.items}
                  />
                )
              case "world_map":
                return <WorldMap />
              case "global_ad":
                return <>{globalAds && <LocationBanners items={globalAds} />}</>
              case "trips_destination_specials":
                return (
                  <Container maxWidth={1480} marginTop="40px!important">
                    <TripCollection
                      currentPage={1}
                      onPageChange={fetchSpecialTripsByDestination}
                      {...specialTrips}
                    />
                  </Container>
                )
              case "trips_specials":
                return <TripGeneralSearch isSpecialsSearch slug={pageSlug} />
              case "main_search":
              case "trips_search":
              default:
                return null
            }
          })}
        <PreFooterSliceResolver id={`preFooterStyle${landingPage?.pre_footer_sid}`} />
      </Layout>
    </>
  )
}

export const ComposeHead = (props) => {
  const { nodeLandingPage } = props.data
  const metatag = get(nodeLandingPage, "metatag.items")
  const url = get(nodeLandingPage, "slug")
  const image = get(nodeLandingPage, "hero.images[0].variants[0].url")
  return <SEOHead metas={metatag} url={url} image={image} />
}

export const query = graphql`
  query LandingPageQuery($id: String!) {
    nodeLandingPage(id: { eq: $id }) {
      id
      slug
      title
      pre_footer_sid
      metatag {
        items {
          content
          name
        }
      }
      parent_page {
        id
        title
        slug
      }
      destinations {
        external_id
      }
      hero {
        ...HeroFragment
      }
      components {
        id
        type
        ... on paragraph__text_overview {
          ...TextOverviewFragment
        }
        ... on paragraph__parallax_media {
          ...ParallaxMediaFragment
        }
        ... on paragraph__benefits_list {
          ...BenefitsListFragment
        }
        ... on paragraph__trip_styles {
          ...TripStylesFragment
        }
        ... on paragraph__places_slideshow {
          ...PlacesSlideshow
        }
        ... on paragraph__contact_cta {
          ...ContactCtaFragment
        }
        ... on paragraph__teaser_3up {
          ...Teaser3UpFragment
        }
        ... on paragraph__suggested_trips {
          ...SuggestedTripsFragment
        }
        ... on paragraph__content_highlight {
          ...ContentHighlightFragment
        }
        ... on paragraph__image_overview {
          ...ImageOverviewFragment
        }
        ... on paragraph__award_list {
          ...AwardsListFragment
        }
        ... on paragraph__stacked_cta {
          ...StackedCtaFragment
        }
        ... on paragraph__destination_collection {
          ...DestinationCollectionFragment
        }
        ... on paragraph__explore_list {
          ...ExploreListFragment
        }
        ... on paragraph__banner_cta {
          ...BannerCTAFragment
        }
        ... on paragraph__careers_list {
          ...CareersListFragment
        }
        ... on paragraph__visual_media_content {
          ...VisualMediaContentFragment
        }
        ... on paragraph__featured_logos {
          ...FeaturedLogosFragment
        }
        ... on paragraph__perks_list {
          ...PerksListFragment
        }
        ... on paragraph__staff_slideshow {
          ...StaffSlideshowFragment
        }
        ... on paragraph__text {
          ...TextFragment
        }
        ... on paragraph__section_switcher {
          ...SectionSwitcherFragment
        }
        ... on paragraph__flex_card_deck {
          ...FlexCardDeckFragment
        }
        ... on paragraph__resource_collection {
          ...ResourceCollectionFragment
        }
      }
    }
  }
`
